import { useState } from "react";
import { X } from "lucide-react";
import { Reveal } from "./Reveal";

// Photos removed for now — add each category's 6 photos here when ready
const PHOTO_ALT = {
  "residential-11.jpeg": "Paving crew with raised dump truck and asphalt paver on a residential driveway job in Northeast Ohio",
  "residential-9.jpeg": "Freshly paved brick home driveway by Trumbull Paving Co.",
  "residential-3.jpeg": "Dump truck feeding LeeBoy asphalt paver on a residential driveway paving project",
  "residential-1.jpeg": "Long freshly paved country driveway in Trumbull County, Ohio",
  "residential-5.jpeg": "Paving crew with dump truck laying fresh asphalt driveways at a residential townhouse community",
  "residential-12.jpeg": "Freshly paved and hand-finished residential driveway with a smooth clean surface",
  "commercial-1.jpeg": "Crew paving an outdoor basketball court with fresh asphalt",
  "commercial-5.jpeg": "Commercial parking lot paving at a municipal building",
  "commercial-13.jpeg": "Dump truck and paver resurfacing a Giant Eagle supermarket parking lot",
  "commercial-21.jpeg": "Fresh yellow and blue handicap parking lot striping on new asphalt",
  "commercial-15.jpeg": "Tri-Axle dump truck feeding asphalt paver with roller and crew on an industrial repaving project",
  "commercial-23.jpeg": "Paving crew hand-raking fresh asphalt at a Taco Bell parking lot",
  "commercial-26.jpeg": "Freshly paved large commercial parking lot in front of a retail building with covered entrance",
  "commercial-20.jpeg": "Freshly paved commercial parking lot with new blue handicap striping and posts",
  "commercial-17.jpeg": "Fresh handicap parking space striping on a newly paved commercial lot",
  "industrial-6.jpeg": "LeeBoy roller compacting fresh asphalt on an industrial lot",
  "industrial-7.jpeg": "Newly paved commercial building lot with fresh yellow striping",
  "industrial-8.jpeg": "Dump truck feeding asphalt paver with paving crew on a large job site",
  "industrial-5.jpeg": "Large freshly paved industrial parking lot",
  "industrial-9.jpeg": "Freshly paved large parking lot with smooth new asphalt",
  "industrial-10.jpeg": "Freshly paved industrial facility lot with new asphalt",
  "industrial-13.jpeg": "Freshly paved asphalt alley between brick industrial buildings",
  "industrial-14.jpeg": "Freshly paved industrial parking lot beside a building with a large mural",
  "industrial-3.jpeg": "Crew hand-raking hot asphalt near an overpass on an industrial paving job",
  "industrial-4.jpeg": "Repaved industrial facility lot between buildings",
};

const CATEGORIES = [
  { name: "Residential", photos: ["/gallery/residential-11.jpeg", "/gallery/residential-12.jpeg", "/gallery/residential-3.jpeg", "/gallery/residential-1.jpeg", "/gallery/residential-5.jpeg", "/gallery/residential-9.jpeg"] },
  { name: "Commercial", photos: ["/gallery/commercial-1.jpeg", "/gallery/commercial-5.jpeg", "/gallery/commercial-13.jpeg", "/gallery/commercial-26.jpeg", "/gallery/commercial-23.jpeg", "/gallery/commercial-21.jpeg"] },
  { name: "Industrial", photos: ["/gallery/commercial-15.jpeg", "/gallery/industrial-13.jpeg", "/gallery/industrial-8.jpeg", "/gallery/industrial-14.jpeg", "/gallery/industrial-6.jpeg", "/gallery/industrial-10.jpeg"] },
];

// Per-photo square-crop focus (object-position) — industrial-10 shifted left so the patched pothole shows
const OBJECT_POS = { "industrial-10.jpeg": "20% 50%" };

// Every gallery photo in display order: Residential → Commercial → Industrial (used by the work strip below the estimate form)
export const WORK_STRIP_PHOTOS = CATEGORIES.flatMap((c) => c.photos);
export { PHOTO_ALT, OBJECT_POS };

// Desktop (3-col) visual order per owner's layout; mobile keeps the curated 2-col order.
// Desktop row 1: bottom-right, middle-left, bottom-left — row 2: top-right, middle-right, top-left (of the mobile grid)
const LG_ORDER = ["lg:order-1", "lg:order-4", "lg:order-2", "lg:order-5", "lg:order-3", "lg:order-6"];

export const Gallery = () => {
  const [selected, setSelected] = useState(null);
  const [cat, setCat] = useState(0);
  const category = CATEGORIES[cat];

  const prev = () => setCat((cat + CATEGORIES.length - 1) % CATEGORIES.length);
  const next = () => setCat((cat + 1) % CATEGORIES.length);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="border-t border-black/10 pb-10 pt-6 lg:pb-16 lg:pt-10"
    >
      <div className="mx-auto max-w-xl px-8 lg:max-w-2xl lg:px-6">
        <Reveal>
          <h2 className="text-center text-3xl font-bold text-royal-dark sm:text-4xl" data-testid="gallery-title">
            Gallery
          </h2>
          <div className="mt-2 flex items-center justify-center gap-5 lg:mt-4">
            <button
              data-testid="gallery-prev-button"
              onClick={prev}
              aria-label="Previous category"
              className="text-lg font-normal text-royal-dark transition-colors duration-200 hover:text-royal-dark/50"
            >
              &lt;
            </button>
            <h3
              className="w-40 text-center text-lg font-medium text-royal-dark sm:w-48 sm:text-xl"
              data-testid="gallery-category-title"
            >
              {category.name}
            </h3>
            <button
              data-testid="gallery-next-button"
              onClick={next}
              aria-label="Next category"
              className="text-lg font-normal text-royal-dark transition-colors duration-200 hover:text-royal-dark/50"
            >
              &gt;
            </button>
          </div>
        </Reveal>
        <div
          className="mt-3 grid grid-cols-2 gap-4 sm:gap-6 lg:mt-4 lg:grid-cols-3"
          data-testid="gallery-grid"
        >
          {category.photos.length === 0
            ? Array.from({ length: 6 }, (_, i) => (
                <Reveal key={`empty-${i}`} delay={i * 0.08}>
                  <div
                    data-testid={`gallery-empty-slot-${i + 1}`}
                    className="aspect-square w-full border border-royal-dark/25"
                  />
                </Reveal>
              ))
            : category.photos.map((src, i) => (
                <Reveal key={`${category.name}-${i}`} delay={i * 0.08} className={LG_ORDER[i] || ""}>
                  <button
                    data-testid={`gallery-photo-${i + 1}`}
                    onClick={() => setSelected(src)}
                    className="group block w-full overflow-hidden border border-royal-dark/25"
                    aria-label={`Enlarge photo ${i + 1}`}
                  >
                    <img
                      src={src}
                      alt={PHOTO_ALT[src.split("/").pop()] || `${category.name} paving work — photo ${i + 1}`}
                      style={{ objectPosition: OBJECT_POS[src.split("/").pop()] || "50% 50%" }}
                      className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </button>
                </Reveal>
              ))}
        </div>
      </div>

      {selected && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setSelected(null)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6"
        >
          <button
            data-testid="gallery-close-button"
            onClick={() => setSelected(null)}
            className="absolute right-6 top-6 text-white/80 transition-colors duration-200 hover:text-white"
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={selected}
            alt="Enlarged work photo"
            className="max-h-[85vh] max-w-full border border-white/20 object-contain"
          />
        </div>
      )}
    </section>
  );
};
